import { Field as ChakraField, Fieldset, HStack, Separator, VStack } from '@chakra-ui/react';
import Field from '@components/common/form/Field';
import { MultiPolygonCoordinatesField } from '@components/ui/coordinatesInput/MultiPolygonCoordinatesField';
import { InputNumber } from '@components/ui/input/InputNumber';
import { FormMap } from '@components/ui/map/formMap/FormMap';
import { usePolygonFormMapContext } from '@components/ui/map/formMap/FormMapContext';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHeightSuffix } from '@redux/settings/settings.selectors';
import { getFirstPolygon } from '@utils/map/polygon.utils';
import { FieldProps, useFormikContext } from 'formik';
import { MultiPolygon, Polygon } from 'geojson';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { AbstractFlightPlan } from '@/types/utm/flightPlan.types';

function CoordInput() {
  const { values, errors, touched, setFieldValue } = useFormikContext<Partial<AbstractFlightPlan>>();
  const {
    mapRef,
    unit,
    setUnit,
    updatePolygonCoordinate,
    deletePolygonCoordinate,
    addPolygonCoordinate,
    geometry,
    isPolygonal,
    zoomOnPolygon,
  } = usePolygonFormMapContext();

  const lastPolyRef = useRef<Polygon | undefined>(undefined);

  useEffect(() => {
    if (!isPolygonal(geometry)) return;
    const newPoly = getFirstPolygon(geometry);
    if (!newPoly || isEqual(newPoly, lastPolyRef.current)) return;

    lastPolyRef.current = newPoly;
    const newFieldValue: MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: [newPoly.coordinates],
    };
    void setFieldValue('polygons', newFieldValue);
  }, [geometry, isPolygonal, setFieldValue]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !values.polygons?.coordinates[0]?.length) return;
    const firstPolygon = getFirstPolygon(values.polygons);
    zoomOnPolygon(firstPolygon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fieldset.Root invalid={touched.polygons && !!errors.polygons} flex={1} minHeight={0}>
      <Fieldset.Content height="100%">
        <ScrollArea>
          <Field name="polygons" height="100%">
            {({ field }: FieldProps<AbstractFlightPlan['polygons']>) => (
              <VStack gap={0} alignItems="flex-start" width="100%" height="100%">
                <MultiPolygonCoordinatesField
                  {...field}
                  name="polygons"
                  unit={unit}
                  setUnit={setUnit}
                  setPolygon={updatePolygonCoordinate}
                  deleteCoordinate={deletePolygonCoordinate}
                  addCoordinate={addPolygonCoordinate}
                />
              </VStack>
            )}
          </Field>
        </ScrollArea>
      </Fieldset.Content>
    </Fieldset.Root>
  );
}

export default function FlightPlanFormMap() {
  const { values, errors, touched, setFieldValue } = useFormikContext<AbstractFlightPlan>();
  const showHeightSuffix = useAppSelector(selectHeightSuffix);

  return (
    <FormMap polygon={values.polygons} mode={values.id ? 'edit' : 'create'}>
      <VStack
        position="absolute"
        borderRight="2px solid"
        borderColor="neutral.black"
        backgroundColor="neutral.700"
        left={0}
        height="100%"
        flexShrink={0}
        zIndex={3}
        gap={0.25}
        separator={<Separator size="md" borderColor="neutral.800" marginY={0} />}
      >
        <HStack width="100%" gap={3} padding={2} flexShrink={0}>
          <ChakraField.Root
            variant="cockpit"
            flexFlow="row"
            alignItems="center"
            required
            invalid={touched.heightMin && !!errors.heightMin}
          >
            <ChakraField.Label height="20px" flexShrink={0}>
              <FormattedMessage id="components.flightPlans.heightMin" />
              {showHeightSuffix && <FormattedMessage id="target.agl" />}
            </ChakraField.Label>
            <Field
              as={InputNumber}
              type="text"
              name="heightMin"
              id="heightMin"
              width="96px"
              min={0}
              allowMouseWheel
              onChange={(value?: number) => {
                setFieldValue('heightMin', value);
                setFieldValue('minAltitudeFinite', value !== undefined);
              }}
              suffix="m"
            />
          </ChakraField.Root>
          <ChakraField.Root
            variant="cockpit"
            flexFlow="row"
            alignItems="center"
            required
            invalid={touched.heightMax && !!errors.heightMax}
          >
            <ChakraField.Label height="20px" flexShrink={0}>
              <FormattedMessage id="components.flightPlans.heightMax" />
              {showHeightSuffix && <FormattedMessage id="target.agl" />}
            </ChakraField.Label>
            <Field
              as={InputNumber}
              type="text"
              name="heightMax"
              id="heightMax"
              width="96px"
              min={0}
              allowMouseWheel
              onChange={(value?: number) => {
                setFieldValue('heightMax', value);
                setFieldValue('maxAltitudeFinite', value !== undefined);
              }}
              suffix="m"
            />
          </ChakraField.Root>
        </HStack>
        <CoordInput />
      </VStack>
    </FormMap>
  );
}
